import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { LanguageProvider } from "@/shared/contexts/LanguageContext";
import { AuthProvider } from "@/features/auth/contexts/AuthContext";
import { ClientLockProvider } from "@/features/auth/contexts/ClientLockContext";
import { PinPromptModal } from "@/features/auth/components/PinPromptModal";
import { ProtectedRoute } from "@/features/auth/components/ProtectedRoute";
import { AppLayout } from "@/shared/components/AppLayout";
import { SyncProvider } from "@/shared/components/SyncProvider";
import { StudioProvider } from '@/features/studio/contexts/StudioContext';
import { ErrorBoundary } from "@/shared/components/ErrorBoundary";
import { useSubscription } from "@/features/subscription/hooks/useSubscription";
import Dashboard from "@/features/dashboard/pages/Dashboard";
import Templates from "@/features/templates/pages/Templates";
import ContractForm from "@/features/contracts/pages/ContractForm";
import History from "@/features/history/pages/History";
import SettingsPage from "@/features/settings/pages/Settings";
import TemplateEditor from "@/features/templates/pages/TemplateEditor";
import InstallPage from "@/features/pwa/pages/InstallPage";
import Pricing from "@/features/subscription/pages/Pricing";
import LoginPage from "@/features/auth/pages/LoginPage";
import RegisterPage from "@/features/auth/pages/RegisterPage";
import ClientsPage from "@/features/clients/pages/Clients";
import ClientDetail from "@/features/clients/pages/ClientDetail";
import MemosPage from "@/features/memos/pages/Memos";
import PostCarePage from "@/features/postcare/pages/PostCare";
import AppointmentsPage from "@/features/appointments/pages/Appointments";
import InventoryPage from "@/features/inventory/pages/Inventory";
import SessionsPage from "@/features/sessions/pages/Sessions";
import SessionDetail from "@/features/sessions/pages/SessionDetail";
import SessionWizard from "@/features/sessions/pages/SessionWizard";
import InvoicesPage from "@/features/invoices/pages/Invoices";
import InvoiceNew from "@/features/invoices/pages/InvoiceNew";
import QuotesPage from "@/features/quotes/pages/Quotes";
import QuoteNew from "@/features/quotes/pages/QuoteNew";
import StudioDashboard from '@/features/studio/pages/StudioDashboard';
import MemberProfile from '@/features/studio/pages/MemberProfile';
import JoinStudio from '@/features/studio/pages/JoinStudio';
import PipelinePage from '@/features/pipeline/pages/Pipeline';
import NotFound from "@/shared/pages/NotFound";
import ResetPassword from "@/features/auth/pages/ResetPassword";
import { CookieConsentBanner } from "@/shared/components/CookieConsent";
import TrialExpired from "@/features/subscription/pages/TrialExpired";
import PublicConsentForm from "@/features/contracts/pages/PublicConsentForm";

const queryClient = new QueryClient();

const TRIAL_EXEMPT = ['/trial-expired', '/pricing', '/settings', '/auth', '/reset-password', '/install', '/join', '/consent'];

function SubscriptionGuard({ children }: { children: React.ReactNode }) {
  const { tier, loading } = useSubscription();
  const { pathname } = useLocation();
  const isExempt = TRIAL_EXEMPT.some(p => pathname.startsWith(p));

  if (loading && (!isExempt || pathname === '/trial-expired')) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-6 h-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }
  if (!loading && tier === 'free' && !isExempt) return <Navigate to="/trial-expired" replace />;
  if (!loading && tier !== 'free' && pathname === '/trial-expired') return <Navigate to="/" replace />;
  return <>{children}</>;
}

function ProtectedWithLayout({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute>
      <AppLayout>{children}</AppLayout>
    </ProtectedRoute>
  );
}

const App = () => (
  <ErrorBoundary>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <BrowserRouter>
          <LanguageProvider>
            <ClientLockProvider>
              <Toaster />
              <Sonner />
              <StudioProvider>
                <SyncProvider>
                  <PinPromptModal />
                  <CookieConsentBanner />
                  <SubscriptionGuard>
                  <Routes>
                    <Route path="/auth" element={<Navigate to="/auth/login" replace />} />
                    <Route path="/auth/login" element={<LoginPage />} />
                    <Route path="/auth/register" element={<RegisterPage />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/install" element={<InstallPage />} />
                    <Route path="/consent/:token" element={<PublicConsentForm />} />
                    <Route path="/trial-expired" element={<ProtectedRoute><TrialExpired /></ProtectedRoute>} />
                    <Route path="/" element={<ProtectedWithLayout><Dashboard /></ProtectedWithLayout>} />
                    <Route path="/templates" element={<ProtectedWithLayout><Templates /></ProtectedWithLayout>} />
                    <Route path="/contract/:templateId" element={<ProtectedWithLayout><ContractForm /></ProtectedWithLayout>} />
                    <Route path="/clients" element={<ProtectedWithLayout><ClientsPage /></ProtectedWithLayout>} />
                    <Route path="/clients/:id" element={<ProtectedWithLayout><ClientDetail /></ProtectedWithLayout>} />
                    <Route path="/history" element={<ProtectedWithLayout><History /></ProtectedWithLayout>} />
                    <Route path="/settings" element={<ProtectedWithLayout><SettingsPage /></ProtectedWithLayout>} />
                    <Route path="/pricing" element={<ProtectedWithLayout><Pricing /></ProtectedWithLayout>} />
                    <Route path="/memos" element={<ProtectedWithLayout><MemosPage /></ProtectedWithLayout>} />
                    <Route path="/post-care" element={<ProtectedWithLayout><PostCarePage /></ProtectedWithLayout>} />
                    <Route path="/appointments" element={<ProtectedWithLayout><AppointmentsPage /></ProtectedWithLayout>} />
                    <Route path="/inventory" element={<ProtectedWithLayout><InventoryPage /></ProtectedWithLayout>} />
                    <Route path="/sessions/new" element={<ProtectedWithLayout><SessionWizard /></ProtectedWithLayout>} />
                    <Route path="/sessions" element={<ProtectedWithLayout><SessionsPage /></ProtectedWithLayout>} />
                    <Route path="/sessions/:id" element={<ProtectedWithLayout><SessionDetail /></ProtectedWithLayout>} />
                    <Route path="/invoices/new" element={<ProtectedWithLayout><InvoiceNew /></ProtectedWithLayout>} />
                    <Route path="/invoices" element={<ProtectedWithLayout><InvoicesPage /></ProtectedWithLayout>} />
                    <Route path="/quotes/new" element={<ProtectedWithLayout><QuoteNew /></ProtectedWithLayout>} />
                    <Route path="/quotes" element={<ProtectedWithLayout><QuotesPage /></ProtectedWithLayout>} />
                    <Route path="/editor/:templateId" element={<ProtectedWithLayout><TemplateEditor /></ProtectedWithLayout>} />
                    <Route path="/pipeline" element={<ProtectedWithLayout><PipelinePage /></ProtectedWithLayout>} />
                    <Route path="/studio" element={<ProtectedWithLayout><StudioDashboard /></ProtectedWithLayout>} />
                    <Route path="/studio/members/:userId" element={<ProtectedWithLayout><MemberProfile /></ProtectedWithLayout>} />
                    <Route path="/join" element={<JoinStudio />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                  </SubscriptionGuard>
                </SyncProvider>
              </StudioProvider>
            </ClientLockProvider>
          </LanguageProvider>
        </BrowserRouter>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
